/*@flow*/
/** @jsx jsx */
import { css, jsx, Global } from '@emotion/core';
import { Fragment } from 'react';
import toast from 'lib/toast';
import type { ContextT } from './types';
import * as I from 'immutable';
import isSmallScreen from 'lib/isSmallScreen';
import PATHS from 'const/paths';
import { state, q } from 'ops';
import Blank from 'lib/Blank';
import addFooWhenTypesEmpty from './addFooWhenTypesEmpty';
import resourceFilterNames from './resourceFilterNames';
import getFilterName from './getFilterName';
import panels from './panels';
import type { ProjectT } from 'comps/ProjectPopup/types';
import removePathsFromQueryMap from 'lib/removePathsFromQueryMap';
import showOnMap from 'lib/showOnMap';
import history from 'lib/history';

export default function commands(ctx: ContextT) {
  function isLayerActive(layer: I.Map<string, any>) {
    const key = layer.get('uuid');
    return key && ctx.state.layersActiveLayers.has(key);
  }
  const showLocationPanel = () => {
    commands(ctx).set(['activePanel'], panels.LOCATION_DETAILS);
  };

  function set(keyPath: string | string[], value: any) {
    keyPath = normalizeKP(keyPath);
    let type = typeof value === 'function' ? 'UPDATE_KEYPATH' : 'SET_KEYPATH';
    ctx.dispatch({
      type,
      payload: { keyPath, value },
    });
  }

  function historyShot() {
    commands(ctx).set(['HISTORY_SHOT'], {
      filters: history.getQuery().filter,
      layers: ctx.state.layersActiveLayers,
    });
  }

  function update(keyPath: string | string[], value: (any) => any) {
    keyPath = normalizeKP(keyPath);
    ctx.dispatch({
      type: 'UPDATE_KEYPATH',
      payload: { keyPath, value },
    });
  }

  return {
    set,
    setIn: set,
    update,
    historyShot,
    updateIn: update,
    remove(keyPath: string | string[]) {
      keyPath = normalizeKP(keyPath);
      ctx.dispatch({
        type: 'REMOVE_KEYPATH',
        payload: { keyPath },
      });
    },
    setFilterQuery(input: ?string) {
      q((v) => v.setIn(['filter', 'q'], input));
    },
    openPanel(name: any) {
      if (!ctx.state.activePanel) {
        const historyStore = ctx.state.HISTORY_SHOT;
        let layers = {};
        let filters = null;
        if (historyStore) {
          layers = historyStore.layers || {};
          filters = historyStore.filters || null;
        }
        if (filters) {
          commands(ctx).updateGlobalFilters(filters);
        }
        if (layers.size) {
          commands(ctx).set(['layersActiveLayers'], layers);
        }
      }
      commands(ctx).set(['activePanel'], name);
    },
    togglePanel(name: any) {
      if (ctx.state.activePanel === name) {
        commands(ctx).closePanel();
      } else {
        commands(ctx).openPanel(name);
      }
    },
    openLocationPanelToasterOnSmallScreens() {
      if (!isSmallScreen()) return;
      toast(
        <Fragment>
          <Global
            styles={css(`
          #toaster > div { background-color: rgb(17, 129, 251) !important; }
          #dismiss-toaster { display: none !important; }
        `)}
          />
          <i className="fa fa-map-marker" />{' '}
          <button
            className="text-white font-weight-bold"
            css={css('text-transform: uppercase;')}
            onClick={showLocationPanel}
          >
            Show Location Details
          </button>
        </Fragment>
      );
    },
    openLocationPanelOnLargeScreens() {
      if (isSmallScreen()) return;
      showLocationPanel();
    },
    clearItem() {
      commands(ctx).set(['focusedResourceId'], null);
      commands(ctx).set(['focusedResourceType'], null);
      commands(ctx).set(['focusedResourceColor'], null);
    },
    closePanel() {
      commands(ctx).clearItem();
      commands(ctx).set(['activePanel'], null);
      commands(ctx).historyShot();
    },
    layerToggle(uuid: string) {
      const isActive = ctx.state.layersActiveLayers.has(uuid);
      const updater = (v) =>
        isActive ? v.filter((v2) => v2 !== uuid) : v.add(uuid);

      commands(ctx).update(['layersActiveLayers'], updater);
    },
    putMarkerAt(lat: number, lon: number) {
      const polygonMode = state(PATHS.areaPolygonModeType);

      if (['Drawing'].includes(polygonMode)) return;
      // Set the property data location
      commands(ctx).set(['focusedLatlon'], { lat, lon });

      // Add a marker
      const latlon = [lat, lon];
      const newMarker = I.fromJS({ type: 'marker', latlon });
      commands(ctx).set(['decks', 'marker'], newMarker);
    },
    dropPinAndPanTo(googPlace: Goog$PlaceT) {
      const arr = getLatlonOfGoogPlace(googPlace);
      if (Blank.is(arr)) return;
      const [lat, lon] = arr;

      // Forcefully center the map to this location.
      const latLngs = [arr];
      // $FlowFixMe[incompatible-call]
      const markerBounds = L.latLngBounds(latLngs);
      getMap().fitBounds(markerBounds);
      getMap().setZoom(16);

      showOnMap({ latlon: [lat, lon] });
      commands(ctx).putMarkerAt(lat, lon);
    },
    panTo(lat: number, lon: number) {
      panMapTo(lat, lon);
    },
    clearFilters() {
      commands(ctx).updateGlobalFilters({ type: [] });
      // HACK: Needed to trigger useFilters.
      commands(ctx).set(['searchKeywordInput'], ' ');
      commands(ctx).set(['searchKeywordInput'], '');
      commands(ctx).set(['searchAddressInput'], '');
      commands(ctx).setFilterQuery('');
    },
    mapClicked(lat: number, lon: number) {
      commands(ctx).putMarkerAt(lat, lon);
      if (!ctx.state.activePanel) {
        commands(ctx).openLocationPanelOnLargeScreens();
        commands(ctx).openLocationPanelToasterOnSmallScreens();
      }
      commands(ctx).set(['focusedResourceId'], null);
    },
    removeMarker() {
      commands(ctx).update(['decks'], (v) =>
        v.filterNot((_, k) => k === 'marker')
      );
    },
    showItemView(v: $Shape<ProjectT & { type?: string }>) {
      const { id, resourceId, pageType, type, fillColor, latlon } = v;

      if (latlon) {
        const [lat, lon] = latlon;
        const foc = ctx.state.focusedLatlon;
        commands(ctx).putMarkerAt(lat, lon);

        if (foc) {
          // If the marker is currently not at latlon, do the following...
          if (foc.lat !== lat && foc.lon !== lon) {
            showOnMap({ id, latlon });
          }
        } else {
          showOnMap({ id, latlon });
        }
      }

      commands(ctx).set(['focusedResourceId'], resourceId);
      commands(ctx).set(['focusedResourceType'], pageType || type);
      commands(ctx).set(['focusedResourceColor'], fillColor);
      commands(ctx).openLocationPanelOnLargeScreens();
    },
    showListView(props: { focusedLatlon: { lat: number, lon: number } } = {}) {
      const { focusedLatlon } = props;
      commands(ctx).set(['focusedResourceId'], null);
      commands(ctx).set(['focusedLatlon'], focusedLatlon);
      commands(ctx).openPanel(panels.LOCATION_DETAILS);
    },
    updateGlobalFilters(filters: I.Map<string, any> | {}) {
      filters = I.fromJS(filters);
      filters = addFooWhenTypesEmpty(filters);

      const typeFilters = filters
        .get('type', I.List())
        .map(getFilterName)
        .filter((v) => resourceFilterNames.includes(v));

      // Remove disabled resource filters from the new filters.
      filters = filters.filter(
        (_, k) => typeFilters.includes(k) || k === 'type'
      );

      // Remove the disabled resource filters from query string filter key
      // and merge filters.
      const filterUpdater = (v) => {
        return (v || I.Map())
          .filterNot((_, k) => resourceFilterNames.includes(k))
          .merge(filters);
      };

      const globalUpdater = (v) =>
        (v || I.Map()).update('filter', filterUpdater);

      // ..in global state.
      q(globalUpdater);
    },
    toggleParcelLayers(isDrawing: boolean) {
      const isParcel = (v) => v && v.get('name', '').match('Parcel');
      const layers = ctx.state.loadedLayers.filter(isParcel);
      layers.forEach((v) => {
        if (isDrawing) {
          commands(ctx).addActiveLayer(v);
        } else {
          commands(ctx).removeActiveLayer(v);
        }
      });
    },
    toggleLayer(layer: I.Map<string, any>) {
      if (isLayerActive(layer)) {
        commands(ctx).removeActiveLayer(layer);
      } else {
        commands(ctx).addActiveLayer(layer);
      }
    },
    addActiveLayer(layer: I.Map<string, any>) {
      // Add layer to active layers, only if the layer is loaded.
      const addActive = (v) => {
        const uuid = layer.get('uuid');
        const isLoaded = ctx.state.layersLoaded
          .map((v2) => v2.uuid)
          .includes(uuid);
        return isLoaded ? v : v.add(uuid);
      };
      commands(ctx).update(['layersActiveLayers'], addActive);
    },
    removeActiveLayer(layer: I.Map<string, any>) {
      // Remove layer from active layers
      const deleteActive = (v) => v.delete(layer.get('uuid'));
      // TODO: adjust the type
      commands(ctx).update(['layersActiveLayers'], deleteActive);
      // Remove key(s) from query string
      let qy = layer.get('query');
      if (!qy) return;
      q((v) => removePathsFromQueryMap(v || I.Map(), qy));
    },
  };
}

function getMap() {
  return window.cityMap || window.districtMap;
}

function getLatlonOfGoogPlace(place) {
  const geometry = place.geometry;
  if (!geometry) return [];
  const loc = geometry.location;
  return [loc.lat(), loc.lng()];
}

function panMapTo(lat, lon) {
  if (!getMap()) return;
  const latlon = [lat, lon];
  if (Blank.is(latlon)) return;
  getMap().panTo(latlon, { animate: true });
}

const normalizeKP = (keyPath) => {
  if (typeof keyPath === 'string') return keyPath.split('.');
  if (Array.isArray(keyPath)) return keyPath;
  throw new TypeError(
    'Expected keyPath to be a string or an array, but it was ' + typeof keyPath
  );
};
